import {
  ConsumerSpendRadiusData,
  DemoDatum,
  DemoRadiiData,
  DemoRadiiRequest,
  DemoRadiiResponse,
  MultiPolygon,
} from 'src/common-services'
import omit from 'lodash/omit'
import { atom, selector } from 'recoil'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector, uncachedSelectorFamily } from 'src/utils/recoil'
import { consumerSpendClient, demoClient } from 'src/clients'
import { googlePlace } from 'src/atoms/search'
import upperFirst from 'lodash/upperFirst'
import { geoJSON, hasCustomArea } from 'src/atoms/custom-area'

const key = keyFactory('demographicz')

export const SPEND_DATA_KEY = 'Overall average annual household expenditure'
export const AGE_DATA_KEY = 'Age'

export const demographicsData = uncachedSelectorFamily<DemoRadiiResponse, DemoRadiiRequest>({
  key: key('census-breakdown'),
  get:
    (req) =>
    async ({ get }) => {
      get(retryAtom)
      const [consumerSpendResults, demoResults] = await Promise.all([
        consumerSpendClient.getByRadii(req),
        demoClient.getByRadii(req),
      ])
      for (const res of demoResults) {
        const spendResult = consumerSpendResults.find((r) => r.radius === res.radius)
        if (!spendResult) {
          continue
        }
        attachSpendToDemographics(res.data, spendResult.data)
        res.data = computeHispanicPercentage(res.data)
      }
      return demoResults
    },
  lru: DEFAULT_LRU_SIZE,
})

const percentage = (num: number | undefined, denom: number | undefined): number | undefined => {
  if (num == null || denom == null) {
    return undefined
  }
  if (!denom) {
    return undefined
  }
  return (num / denom) * 100
}

export const HISPANIC_PERCENTAGE_KEY = '% Hispanic'

// TODO(bmc): this is gross - we are mutating server results to combine them for the client
// we should clean these types up and normalize in one place
function attachSpendToDemographics(demoData: DemoRadiiData, spendData: ConsumerSpendRadiusData) {
  const spendProperty: Record<string, { currentYear?: number; fiveYear?: number; type: string }> = {}
  for (const [key, val] of Object.entries(spendData.currentYear)) {
    spendProperty[key] = { currentYear: val, type: 'currency' }
  }
  for (const [key, val] of Object.entries(spendData.fiveYear)) {
    spendProperty[key] = { ...spendProperty[key], fiveYear: val }
  }
  demoData[SPEND_DATA_KEY] = spendProperty
}

export function computeHispanicPercentage(demoData: DemoRadiiData): DemoRadiiData {
  if (demoData.Overview && demoData.Ethnicity) {
    const totalPop = demoData.Overview.Population
    const hispanic = demoData.Ethnicity.Hispanic
    if (totalPop && hispanic) {
      const hispanicPercentage: DemoDatum = {
        type: 'percentage',
        currentYear: percentage(hispanic.currentYear, totalPop.currentYear),
        fiveYear: percentage(hispanic.fiveYear, totalPop.fiveYear),
      }
      return {
        ...demoData,
        Ethnicity: omit(
          {
            ...demoData.Ethnicity,
            [HISPANIC_PERCENTAGE_KEY]: hispanicPercentage,
          },
          'Hispanic'
        ),
      }
    }
  }
  return demoData
}

export const areaDemographicsData = uncachedSelector<DemoRadiiData>({
  key: key('area-demo-data'),
  get: async ({ get }) => {
    get(retryAtom)
    const geo = get(geoJSON)
    const polygon: MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: [geo.coordinates],
    }

    const [consumerSpendResults, demoResults] = await Promise.all([
      consumerSpendClient.getByMultiPolygon({ polygon }),
      demoClient.getByMultiPolygon({ polygon }),
    ])

    attachSpendToDemographics(demoResults, consumerSpendResults)
    return computeHispanicPercentage(demoResults)
  },
  lru: DEFAULT_LRU_SIZE,
})

type DataSection = { [key: string]: { type?: string; currentYear?: number; fiveYear?: number } }

export const friendlySpendName = (key: string): string => {
  switch (key) {
    default:
      return key.split('_').map(upperFirst).join(' ')
  }
}

export const sidebarSpendData = selector<DataSection | undefined>({
  key: key('sidebar-spend-data'),
  get: ({ get }) => {
    const demo = get(sidebarDemographics)
    if (!demo) {
      return undefined
    }
    return demo[SPEND_DATA_KEY]
  },
})

export const sidebarRadius = atom({
  key: key('sidebar-radius'),
  default: 3,
})

const requestParams = uncachedSelector<DemoRadiiRequest | undefined>({
  key: key('request-params'),
  get: ({ get }) => {
    const place = get(googlePlace)
    if (!place) {
      return undefined
    }
    const radius = get(sidebarRadius)
    const [lng, lat] = place.geometry.location
    const req = {
      radii: [radius],
      lng,
      lat,
    }
    return req
  },
  lru: DEFAULT_LRU_SIZE,
})

export const sidebarDemographics = selector<DemoRadiiData | undefined>({
  key: key('demo-sidebar-data'),
  get: ({ get }) => {
    const isCustomDraw = get(hasCustomArea)
    if (isCustomDraw) {
      return get(areaDemographicsData)
    }
    const req = get(requestParams)
    if (!req) {
      return undefined
    }
    const [data] = get(demographicsData(req))
    return data.data
  },
})
